export const entitiesDontNeedOrganizationId = ['Organization', 'User', 'Lineup', 'GameStar', 'Attachment', 'ChangeRequest'];
export const BASE_URL = process.env.REACT_APP_BACKEND_BASE_URL;

function isPlainObject(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Table/API rows often include nested relations (Season, Player, …). Bulk PATCH only needs
 * the primary key plus top-level model attributes; sending full rows hits body size limits.
 */
function slimRecordsForBulkPatch(records, entity) {
    const pk = entity.primaryKey;
    const fieldNames = entity.fields && typeof entity.fields === 'object'
        ? new Set(Object.keys(entity.fields))
        : null;

    return records.map((record) => {
        const out = {};
        if (record[pk] != null) {
            out[pk] = record[pk];
        }
        for (const key of Object.keys(record)) {
            if (key === pk || key === 'key') {
                continue;
            }
            const value = record[key];
            if (isPlainObject(value)) {
                continue;
            }
            if (fieldNames && !fieldNames.has(key)) {
                continue;
            }
            out[key] = value;
        }
        return out;
    });
}

export async function postEntity(ORGANIZATION_ID, entity, data, currentRole){
    if (entitiesDontNeedOrganizationId.indexOf(entity.name) == -1) {
        data = { ...data, 'organizationId': ORGANIZATION_ID };
    }
    const url = `${BASE_URL}/admin/entity?entity=${entity.name}&role=${currentRole}`;
    const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
    });
    return {
        status: response.status,
        body: await response.json(),
    }
}

export async function postEntities(ORGANIZATION_ID, entity, data, currentRole){
    if (entitiesDontNeedOrganizationId.indexOf(entity.name) == -1) {
        data = data.map(r => ({ ...r, 'organizationId': ORGANIZATION_ID }));    // Warning: Array (!)
    }
    const url = `${BASE_URL}/admin/entities?entity=${entity.name}&role=${currentRole}`;
    const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
    });
    return {
        status: response.status,
        body: await response.json(),
    }
}

export async function patchEntity(ORGANIZATION_ID, entity, id, data, currentRole){
    if (entitiesDontNeedOrganizationId.indexOf(entity.name) == -1) {
        data = { ...data, 'organizationId': ORGANIZATION_ID };
    }
    const url = `${BASE_URL}/admin/entity?entity=${entity.name}&id=${id}&role=${currentRole}`;
    const response = await fetch(url, {
        method: 'PATCH',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
    });
    return {
        status: response.status,
        body: await response.json(),
    }
}

export async function patchEntities(ORGANIZATION_ID, entity, data, currentRole){
    let payload = data;//slimRecordsForBulkPatch(data, entity);
    if (entitiesDontNeedOrganizationId.indexOf(entity.name) == -1) {
        payload = payload.map(r => ({ ...r, 'organizationId': ORGANIZATION_ID }));    // Warning: Array (!)
    }
    const url = `${BASE_URL}/admin/entities?entity=${entity.name}&role=${currentRole}`;
    const response = await fetch(url, {
        method: 'PATCH',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
    });
    return {
        status: response.status,
        body: await response.json(),
    }
}

export async function putEntity (ORGANIZATION_ID, entity, data, currentRole){
    if (entitiesDontNeedOrganizationId.indexOf(entity.name) == -1) {
        data = { ...data, 'organizationId': ORGANIZATION_ID };
    }
    const url = `${BASE_URL}/admin/entity?entity=${entity.name}&id=${data[entity.primaryKey]}&role=${currentRole}`;
    const response = await fetch(url, {
        method: 'PUT',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
    });

    return {
        status: response.status,
        body: await response.json(),
    }
}

export async function deleteEntity(entity, id, currentRole){
    const url = `${BASE_URL}/admin/entity?entity=${entity.name}&id=${id}&role=${currentRole}`;
    const response = await fetch(url, {
        method: 'DELETE',
        credentials: 'include',
    });
    return {
        status: response.status,
    }
}

export async function cloneEntity(entity, id, data, currentRole){
    const url = `${BASE_URL}/admin/cloneEntity?entity=${entity.name}&id=${id}&role=${currentRole}`;
    const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
    });
    return {
        status: response.status,
        body: await response.json(),
    }
}

export async function deleteEntities(entity, ids, currentRole){
    const url = `${BASE_URL}/admin/entities?entity=${entity.name}&role=${currentRole}`;
    const response = await fetch(url, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ids: ids }),
    });
    return {
        status: response.status,
    }
}

export async function postRelation(sourceEntity, sourceEntityId, targetEntity, targetEntityIds, targetEntityAs, currentRole){
    const url = `${BASE_URL}/admin/entityRelation?` +
        `sourceEntity=${sourceEntity.name}&` +
        `sourceEntityId=${sourceEntityId}&` +
        `targetEntity=${targetEntity.name}&` +
        `targetEntityAs=${targetEntityAs ?? targetEntity.name}&` +
        `targetEntityId=${Array.isArray(targetEntityIds) ? targetEntityIds.join(',') : targetEntityIds}&` +
        `role=${currentRole}`;

    const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
    });
    return {
        status: response.status,
        body: await response.json(),
    }
}

export async function deleteRelation(sourceEntity, sourceEntityId, targetEntity, targetEntityIds, targetEntityAs, currentRole){
    const url = `${BASE_URL}/admin/entityRelation?` +
        `sourceEntity=${sourceEntity.name}&` +
        `sourceEntityId=${sourceEntityId}&` +
        `targetEntity=${targetEntity.name}&` +
        `targetEntityAs=${targetEntityAs ?? targetEntity.name}&` +
        `targetEntityId=${Array.isArray(targetEntityIds) ? targetEntityIds.join(',') : targetEntityIds}&` +
        `role=${currentRole}`;

    const response = await fetch(url, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
    });
    return {
        status: response.status,
        body: await response.json(),
    }
}

export async function postSchedulerGenerateGames(id, currentRole){
    const url = `${BASE_URL}/scheduler/generateGames?id=${id}&role=${currentRole}`;
    const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
    });
    return {
        status: response.status,
        body: await response.json(),
    }
}

export async function postLogo(blob, parameters, currentRole){
    const urlParams = new URLSearchParams(parameters);
    urlParams.append('role', currentRole);
    const formData = new FormData();
    formData.append('file', blob);
    
    return await fetch(`${BASE_URL}/admin/logo?${urlParams.toString()}`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });
}

export async function patchLogo(parameters, currentRole){
    const urlParams = new URLSearchParams(parameters);
    urlParams.append('role', currentRole);
    
    return await fetch(`${BASE_URL}/admin/logo?${urlParams.toString()}`, {
      method: 'PATCH',
      credentials: 'include',
    });
}

export async function deleteLogo(parameters, currentRole){
    const urlParams = new URLSearchParams(parameters);
    urlParams.append('role', currentRole);
    
    return await fetch(`${BASE_URL}/admin/logo?${urlParams.toString()}`, {
      method: 'DELETE',
      credentials: 'include',
    });
}

export async function postAttachment(blob, parameters, currentRole){
    const urlParams = new URLSearchParams(parameters);
    urlParams.append('role', currentRole);
    const formData = new FormData();
    formData.append('file', blob);
    
    return await fetch(`${BASE_URL}/admin/attachment?${urlParams.toString()}`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });
}

export async function deleteAttachment(parameters, currentRole) {
    const urlParams = new URLSearchParams(parameters);
    urlParams.append('role', currentRole);
    
    return await fetch(`${BASE_URL}/admin/attachment?${urlParams.toString()}`, {
      method: 'DELETE',
      credentials: 'include',
    });
}

export async function sendInvite(data, currentRole) {
    const url = `${BASE_URL}/admin/sendInvite?role=${currentRole}`;
    return await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
    });
}

export async function bulkSendInvite(data, currentRole) {
    const url = `${BASE_URL}/admin/bulkSendInvite?role=${currentRole}`;
    return await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
    });
}

export async function bulkInviteToSeason(data, currentRole) {
    const url = `${BASE_URL}/admin/bulkInviteToSeason?role=${currentRole}`;
    return await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
    });
}

export async function postFileUpload(blob, parameters, currentRole){
    const urlParams = new URLSearchParams(parameters);
    urlParams.append('role', currentRole);
    const formData = new FormData();
    formData.append('file', blob);
    
    return await fetch(`${BASE_URL}/admin/fileUpload?${urlParams.toString()}`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });
}

export async function initiatePayment(data, currentRole) {
    const url = `${BASE_URL}/admin/initiatePayment?role=${currentRole}`;
    return await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
    });
}

// HMS-78: API token of the organization for the read-only integration API (/api/v1)
export async function getOrganizationApiToken(organizationId, currentRole) {
    return await fetch(`${BASE_URL}/admin/organizationApiToken?organizationId=${organizationId}&role=${currentRole}`, {
        credentials: 'include',
    });
}

export async function generateOrganizationApiToken(organizationId, currentRole) {
    return await fetch(`${BASE_URL}/admin/organizationApiToken?organizationId=${organizationId}&role=${currentRole}`, {
        method: 'POST',
        credentials: 'include',
    });
}

export async function revokeOrganizationApiToken(organizationId, currentRole) {
    return await fetch(`${BASE_URL}/admin/organizationApiToken?organizationId=${organizationId}&role=${currentRole}`, {
        method: 'DELETE',
        credentials: 'include',
    });
}

// HMS-78: webhook of the organization (game.finished / game.updated) for the integration API
export async function getOrganizationWebhook(organizationId, currentRole) {
    return await fetch(`${BASE_URL}/admin/organizationWebhook?organizationId=${organizationId}&role=${currentRole}`, {
        credentials: 'include',
    });
}

export async function setOrganizationWebhook(organizationId, currentRole, webhookUrl) {
    return await fetch(`${BASE_URL}/admin/organizationWebhook?organizationId=${organizationId}&role=${currentRole}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ webhookUrl }),
    });
}

export async function deleteOrganizationWebhook(organizationId, currentRole) {
    return await fetch(`${BASE_URL}/admin/organizationWebhook?organizationId=${organizationId}&role=${currentRole}`, {
        method: 'DELETE',
        credentials: 'include',
    });
}

export async function regenerateOrganizationWebhookSecret(organizationId, currentRole) {
    return await fetch(`${BASE_URL}/admin/organizationWebhookSecret?organizationId=${organizationId}&role=${currentRole}`, {
        method: 'POST',
        credentials: 'include',
    });
}

export async function setOrganizationWebhookSecret(organizationId, currentRole, secret) {
    return await fetch(`${BASE_URL}/admin/organizationWebhookSecret?organizationId=${organizationId}&role=${currentRole}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ secret }),
    });
}

export async function testOrganizationWebhook(organizationId, currentRole) {
    return await fetch(`${BASE_URL}/admin/organizationWebhookTest?organizationId=${organizationId}&role=${currentRole}`, {
        method: 'POST',
        credentials: 'include',
    });
}
